import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "General Contracting",
  "Commercial Construction",
  "Residential Renovations",
  "Interior Build-Outs",
  "Electrical Coordination",
  "Project Management",
  "Structural Improvements",
  "Custom Projects",
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A] overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920"
            alt="Contact"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            Contact Us
          </span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-6">
            Let's Build<br />Something Great
          </h1>
          <div className="section-divider" />
          <p className="text-[#A1A1AA] text-xl max-w-2xl mt-6">
            Ready to start your construction project? Schedule a free consultation
            or send us a message.
          </p>
        </div>
      </section>

      {/* Consultation CTA Banner */}
      <section className="py-12 bg-[#7ED4C6]" data-testid="consultation-banner">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-['Oswald'] text-2xl font-bold text-[#0A0A0A]">
                Prefer to Talk? Schedule a Free 30-Minute Consultation
              </h2>
              <p className="text-[#0A0A0A]/80">
                Speak directly with a project manager about your construction needs.
              </p>
            </div>
            <Link
              to="/consultation"
              className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="consultation-banner-btn"
            >
              <Calendar size={18} />
              Book a Call
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#121212]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-['Oswald'] text-3xl font-bold text-white mb-2">
                Send Us a Message
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {isSubmitted ? (
                <div className="bg-[#1E1E1E] p-8 text-center" data-testid="form-success">
                  <CheckCircle size={48} className="text-[#7ED4C6] mx-auto mb-4" />
                  <h3 className="font-['Oswald'] text-2xl font-bold text-white mb-2">
                    Thank You!
                  </h3>
                  <p className="text-[#A1A1AA] mb-6">
                    We've received your message and will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input w-full"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input w-full"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input w-full"
                        placeholder="(123) 456-7890"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm mb-2">Service Interest</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="form-input w-full"
                        data-testid="contact-service"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="form-input w-full resize-none"
                      placeholder="Tell us about your project..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-['Oswald'] text-3xl font-bold text-white mb-8">
                Contact Information
              </h2>

              <div className="space-y-6 mb-12">
                <a
                  href="tel:862-350-7122"
                  className="flex items-start gap-4 p-6 bg-[#1E1E1E] border border-[#2A2A2A] hover:border-[#7ED4C6]/50 transition-colors group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#7ED4C6] flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-[#0A0A0A]" />
                  </div>
                  <div>
                    <h3 className="font-['Oswald'] text-lg font-semibold text-white group-hover:text-[#7ED4C6] transition-colors">
                      Phone
                    </h3>
                    <p className="text-[#A1A1AA]">862-350-7122</p>
                    <p className="text-[#A1A1AA] text-sm mt-1">Call us for immediate assistance</p>
                  </div>
                </a>

                <a
                  href="mailto:estimating@astute-builders.com"
                  className="flex items-start gap-4 p-6 bg-[#1E1E1E] border border-[#2A2A2A] hover:border-[#7ED4C6]/50 transition-colors group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#7ED4C6] flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-[#0A0A0A]" />
                  </div>
                  <div>
                    <h3 className="font-['Oswald'] text-lg font-semibold text-white group-hover:text-[#7ED4C6] transition-colors">
                      Email
                    </h3>
                    <p className="text-[#A1A1AA]">estimating@astute-builders.com</p>
                    <p className="text-[#A1A1AA]">alawn@astute-builders.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-[#1E1E1E] border border-[#2A2A2A]">
                  <div className="w-12 h-12 bg-[#7ED4C6] flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-[#0A0A0A]" />
                  </div>
                  <div>
                    <h3 className="font-['Oswald'] text-lg font-semibold text-white">
                      Business Hours
                    </h3>
                    <p className="text-[#A1A1AA]">Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p className="text-[#A1A1AA]">Saturday: 9:00 AM - 2:00 PM</p>
                    <p className="text-[#A1A1AA]">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Service Area Map */}
              <div>
                <h3 className="font-['Oswald'] text-xl font-semibold text-white mb-4">
                  Service Area
                </h3>
                <div className="map-container aspect-video relative" data-testid="service-map">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <MapPin size={48} className="text-[#7ED4C6]" />
                  </div>
                  <img
                    src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800"
                    alt="New Jersey and New York Service Area"
                    className="w-full h-full object-cover opacity-30"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-['Oswald'] text-lg">Serving New Jersey & New York</p>
                    <p className="text-[#A1A1AA] text-sm">
                      Including Newark, Jersey City, Manhattan, Brooklyn, and surrounding areas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#7ED4C6]" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
            Prefer to Talk?
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-6">
            Call us directly for immediate assistance with your construction needs.
          </p>
          <a
            href="tel:862-350-7122"
            className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
            data-testid="contact-call-btn"
          >
            <Phone size={18} />
            Call 862-350-7122
          </a>
        </div>
      </section>
    </div>
  );
}
